import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PLANS, FREEBIES } from "@/data/content";

const HEADLINE = ["Premium Nutrition.", "Expert Guidance.", "Healthier Habits."];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const OfferCard = ({ plan, selected, onSelect }) => {
  const best = plan.id === "triple";
  return (
    <button
      data-testid={`offer-${plan.id}`}
      onClick={() => onSelect(plan.id)}
      className={`relative text-left rounded-2xl p-5 transition-colors duration-300 w-full ${
        best
          ? "border border-gold/70 gold-glow bg-white/[0.03]"
          : "border border-white/10 hover:border-white/25 bg-white/[0.02]"
      } ${selected ? "ring-1 ring-gold" : ""}`}
    >
      {best && (
        <span className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-obsidian">
          <Star className="h-3 w-3" fill="currentColor" /> {plan.badge}
        </span>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-vmuted">{plan.tagline}</p>
          <p className="font-serif text-2xl text-white mt-1">{plan.title}</p>
          <p className="text-xs text-vmuted mt-0.5">{plan.tabs} tablets · {plan.perTab}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-3xl text-white">₹{plan.price}</p>
          {plan.save && <p className="text-xs text-gold mt-0.5">{plan.save}</p>}
        </div>
      </div>
      <span
        className={`absolute right-4 top-4 h-5 w-5 rounded-full border grid place-items-center transition-colors ${
          selected ? "bg-gold border-gold" : "border-white/30"
        }`}
      >
        {selected && <Check className="h-3 w-3 text-obsidian" strokeWidth={3} />}
      </span>
    </button>
  );
};

export const Hero = () => {
  const { addToCart } = useCart();
  const [selected, setSelected] = React.useState("triple");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative radial-emerald noise overflow-hidden pt-28 md:pt-36 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-gold mb-6"
            >
              ACV Effervescent Tablets · 20 Tabs
            </motion.p>

            <h1 className="font-serif font-medium tracking-tight leading-[1.02] text-white text-4xl sm:text-6xl lg:text-7xl">
              {HEADLINE.map((line, i) => (
                <span key={line} className="hero-line-mask">
                  <motion.span
                    custom={i}
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    className={`block ${i === 2 ? "gold-text" : ""}`}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-7 max-w-xl text-base md:text-lg text-vmuted leading-relaxed"
            >
              Every order includes a free consultation, a personalised diet plan, dedicated coach
              support and app access — at no extra cost.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.6 }}
              className="mt-5 flex flex-wrap gap-x-6 gap-y-2"
            >
              {FREEBIES.map((f) => (
                <span key={f} className="inline-flex items-center gap-2 text-sm text-white/80">
                  <Check className="h-4 w-4 text-gold" strokeWidth={2} /> {f}
                </span>
              ))}
            </motion.div>

            {/* Offers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.7 }}
              className="mt-9 grid sm:grid-cols-2 gap-4 max-w-xl"
            >
              {PLANS.map((p) => (
                <OfferCard key={p.id} plan={p} selected={selected === p.id} onSelect={setSelected} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="mt-7 flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <button
                data-testid="hero-buy-btn"
                onClick={() => addToCart(selected)}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-obsidian font-medium hover:bg-gold-light transition-colors duration-300"
              >
                Add to Cart · ₹{PLANS.find((p) => p.id === selected).price}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-learn-btn"
                onClick={() => document.querySelector("#why")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-8 py-4 text-white hover:border-gold/50 transition-colors duration-300"
              >
                Explore Benefits
              </button>
            </motion.div>
          </div>

          {/* Right: product */}
          <div className="lg:col-span-5 relative">
            <motion.div style={{ y: glowY }} className="absolute inset-0 -z-10 grid place-items-center">
              <div className="h-[70%] w-[70%] rounded-full bg-gold/15 blur-[90px]" />
            </motion.div>
            <motion.div
              style={{ y }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md mx-auto rounded-[2rem] overflow-hidden ring-1 ring-gold/25 gold-glow"
            >
              <img
                src="/assets/product_tube.png"
                alt="Veda Vitals ACV Premium Blend Effervescent Tablets"
                className="w-full h-full object-cover"
                data-testid="hero-product-img"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
